// =====================================================================
// «RECUPERAR MI CUENTA» · ESTABILIDAD F6 · Parte E
// =====================================================================
// Se abre con `/app?ir=recuperar` (desde la pantalla de acceso o desde el
// correo «Recibimos tu petición»). Funciona SIN sesión.
//
// E1 PRIMERO. Antes que el camino largo se ofrece lo que recupera al
// instante: los códigos de respaldo o una llave de acceso (F4), y el
// «olvidé mi contraseña» de siempre. Solo si se perdió todo se pasa al
// camino verificado (E2), que es un asistente paso a paso con su avance
// visible: e.firma (o videollamada) → constancia fiscal → CURP → espera de
// 72 horas y revisión de un operador → contraseña nueva.
import { useEffect, useRef, useState } from 'react';
import { useSubpantalla } from '../ui/navegacionReact';
import {
  KeyRound, LifeBuoy, ShieldAlert, Loader2, CheckCircle2, Circle, FileSignature,
  FileText, IdCard, Hourglass, Copy,
} from 'lucide-react';
import { api } from '../api';
import Ayuda from '../ui/Ayuda';
import CampoContrasena from '../ui/CampoContrasena';
import {
  correoValido, curpValida, faltaParaEmpezar, faltaParaLaRevision, limpiarCurp, limpiarRfc,
  ligaDeSeguimiento, pasoDelEstado, pasosDelCamino, peticionRecordada, recordarPeticion,
} from './recuperacionEnlace';
import { copiarTexto } from '../peaje/copiar';
import { aBase64, cargarForge, ErrorDeFirma, firmarReto, leerArchivo } from './firmaEfirma';

const BOTON = 'inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-lg px-4 py-3 text-base font-semibold disabled:opacity-60';
const PRIMARIO = `${BOTON} bg-[var(--color-marca)] text-white`;
const SECUNDARIO = `${BOTON} border border-gray-300 bg-white text-gray-800`;
const CAMPO = 'mt-1 block min-h-12 w-full rounded-lg border border-gray-300 px-3 py-2 text-base';

function Aviso({ tono = 'rojo', children }) {
  const clase = tono === 'rojo'
    ? 'border-red-200 bg-red-50 text-red-700'
    : tono === 'amarillo' ? 'border-amber-200 bg-amber-50 text-amber-800'
      : 'border-green-200 bg-green-50 text-green-800';
  return <p role={tono === 'rojo' ? 'alert' : 'status'} className={`rounded-lg border p-2.5 text-sm ${clase}`}>{children}</p>;
}

function Avance({ pasos, actual }) {
  return (
    <ol className="flex flex-wrap gap-1.5" aria-label="Avance de la recuperación">
      {pasos.map((p, i) => (
        <li key={p} className={`rounded-full px-2.5 py-1 text-xs font-medium ${
          i < actual ? 'bg-green-100 text-green-800' : i === actual ? 'bg-[var(--color-marca)] text-white' : 'bg-gray-100 text-gray-600'}`}
            aria-current={i === actual ? 'step' : undefined}>
          {i + 1}. {p}
        </li>
      ))}
    </ol>
  );
}

export default function RecuperarMiCuenta({ peticionInicial = null }) {
  const [vista, setVista] = useState(peticionInicial || peticionRecordada() ? 'asistente' : 'inicio');
  useSubpantalla('asistente', vista === 'asistente', () => setVista('inicio'));
  return (
    // Peaje U-02: sigue el tema (consola + lienzo), como CambioPassword.
    <div className="consola min-h-dvh bg-[var(--color-lienzo)] px-4 py-8 text-gray-800">
      <div className="mx-auto max-w-lg space-y-4 rounded-2xl border border-gray-200 bg-white p-5">
        {vista === 'inicio'
          ? <Inicio onVerificada={() => setVista('asistente')} />
          : <Asistente peticionInicial={peticionInicial || peticionRecordada()} />}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------
// E1 · lo que recupera al instante
// ---------------------------------------------------------------------
function Inicio({ onVerificada }) {
  return (
    <>
      <div className="flex items-start gap-3">
        <LifeBuoy className="h-7 w-7 shrink-0 text-[var(--color-marca)]" aria-hidden="true" />
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Recuperar mi cuenta</h1>
          <p className="text-sm text-gray-600">Elige cómo volver a entrar, empezando por lo más rápido.</p>
        </div>
      </div>
      <Ayuda id="seguridad.recuperar.pantalla" />

      <section className="space-y-2 rounded-xl border border-gray-200 p-4">
        <h2 className="flex items-center gap-2 font-semibold text-gray-900">
          <KeyRound className="h-5 w-5" aria-hidden="true" /> Tengo mis códigos de respaldo o una llave de acceso
        </h2>
        <a href="/app?ir=login" className={PRIMARIO}>Entrar con ellos</a>
        <Ayuda id="seguridad.recuperar.codigos" />
      </section>

      <section className="space-y-2 rounded-xl border border-gray-200 p-4">
        <h2 className="font-semibold text-gray-900">Solo olvidé mi contraseña</h2>
        <a href="/app?ir=login" className={SECUNDARIO}>Pedir una contraseña temporal</a>
        <Ayuda id="seguridad.recuperar.olvide" />
      </section>

      <section className="space-y-2 rounded-xl border border-red-200 p-4">
        <h2 className="flex items-center gap-2 font-semibold text-gray-900">
          <ShieldAlert className="h-5 w-5 text-red-600" aria-hidden="true" /> Me robaron la cuenta o perdí todo
        </h2>
        <button type="button" onClick={onVerificada} className={SECUNDARIO}>Empezar la recuperación verificada</button>
        <Ayuda id="seguridad.recuperar.verificada" />
      </section>
    </>
  );
}

// ---------------------------------------------------------------------
// E2 · el asistente
// ---------------------------------------------------------------------
function Asistente({ peticionInicial }) {
  const [camino, setCamino] = useState('EFIRMA');
  const [reto, setReto] = useState(null);          // { reto_token, reto }
  const [peticion, setPeticion] = useState(peticionInicial);
  const [estado, setEstado] = useState(null);
  const [cargandoEstado, setCargandoEstado] = useState(!!peticionInicial);
  const [error, setError] = useState(null);

  // Una petición que ya existe (liga del correo o esta pestaña): se lee su estado.
  useEffect(() => {
    if (!peticionInicial) return;
    let vivo = true;
    api.post('/recuperacion/estado', peticionInicial)
      .then((e) => { if (vivo) { setEstado(e); setCamino(e.camino); } })
      .catch((e) => { if (vivo) setError(e.message); })
      .finally(() => { if (vivo) setCargandoEstado(false); });
    return () => { vivo = false; };
  }, [peticionInicial]);

  function nacio(r) {
    const p = { seguimiento: r.seguimiento, solicitud: r.solicitud, empresa: r.empresa };
    recordarPeticion(p);
    setPeticion(p);
    setEstado(r.estado);
  }

  const pasos = pasosDelCamino(camino);
  const actual = estado ? pasoDelEstado(estado) : reto ? 1 : 0;
  const nombre = pasos[actual];

  return (
    <>
      <div className="flex items-start gap-3">
        <ShieldAlert className="h-7 w-7 shrink-0 text-red-600" aria-hidden="true" />
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Recuperación verificada</h1>
          <p className="text-sm text-gray-600">Paso a paso, para devolver la cuenta del Admin Supremo a su dueño.</p>
        </div>
      </div>
      <Avance pasos={pasos} actual={actual} />
      {error && <Aviso>{error}</Aviso>}
      {estado?.ultimo_rechazo && !estado.cerrada && estado.siguiente && (
        <Aviso tono="amarillo">Último intento: {estado.ultimo_rechazo.mensaje}</Aviso>
      )}

      {cargandoEstado && (
        <p role="status" className="flex items-center gap-2 text-sm text-gray-600">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Leyendo tu petición…
        </p>
      )}
      {!cargandoEstado && nombre === 'Empezar' && (
        <PasoEmpezar camino={camino} setCamino={setCamino} onListo={setReto} />
      )}
      {!cargandoEstado && nombre === 'e.firma' && reto && <PasoEfirma reto={reto} onListo={nacio} />}
      {!cargandoEstado && nombre === 'Constancia fiscal' && (
        <PasoCsf reto={reto} peticion={peticion}
                 onListo={(r) => (r.seguimiento ? nacio(r) : setEstado(r.estado))} />
      )}
      {!cargandoEstado && nombre === 'CURP' && peticion && (
        <PasoCurp peticion={peticion} onListo={(r) => setEstado(r.estado)} />
      )}
      {!cargandoEstado && nombre === 'Espera y revisión' && estado && (
        <PasoEspera estado={estado} peticion={peticion} onEstado={setEstado} />
      )}
      {peticion && <LigaDeSeguimiento peticion={peticion} />}
    </>
  );
}

function PasoEmpezar({ camino, setCamino, onListo }) {
  const [rfc, setRfc] = useState('');
  const [correo, setCorreo] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState(null);
  const falta = faltaParaEmpezar({ rfc, correo });

  async function empezar(ev) {
    ev.preventDefault();
    setEnviando(true);
    setError(null);
    try {
      onListo(await api.post('/recuperacion/iniciar', { rfc, camino, correo: correo.trim() }));
    } catch (e) {
      setError(e.message);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <form onSubmit={empezar} className="space-y-3">
      <fieldset className="space-y-2">
        <legend className="font-medium text-gray-900">¿Tienes la e.firma de la empresa?</legend>
        <label className="flex min-h-12 items-center gap-2 rounded-lg border border-gray-200 px-3">
          <input type="radio" name="camino" checked={camino === 'EFIRMA'} onChange={() => setCamino('EFIRMA')} />
          Sí, tengo mi e.firma (archivos .cer y .key)
        </label>
        <label className="flex min-h-12 items-center gap-2 rounded-lg border border-gray-200 px-3">
          <input type="radio" name="camino" checked={camino === 'VIDEOLLAMADA'} onChange={() => setCamino('VIDEOLLAMADA')} />
          No la tengo: haré una videollamada con identificación oficial
        </label>
        <Ayuda id="seguridad.recuperar.camino" />
      </fieldset>
      <label className="block text-sm font-medium text-gray-800">
        RFC de la empresa <span className="text-red-600" aria-hidden="true">*</span>
        <input className={CAMPO} value={rfc} onChange={(e) => setRfc(limpiarRfc(e.target.value))}
               autoCapitalize="characters" autoComplete="off" inputMode="text" required />
      </label>
      <Ayuda id="seguridad.recuperar.rfc" />
      <label className="block text-sm font-medium text-gray-800">
        Tu correo para esta petición <span className="text-red-600" aria-hidden="true">*</span>
        <input className={CAMPO} type="email" value={correo} onChange={(e) => setCorreo(e.target.value.trim())}
               autoComplete="email" required />
      </label>
      <Ayuda id="seguridad.recuperar.correo" />
      {error && <Aviso>{error}</Aviso>}
      <button type="submit" className={PRIMARIO} disabled={enviando || falta.length > 0}>
        {enviando && <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />} Empezar
      </button>
      {falta.length > 0 && <p className="text-sm text-gray-600">Falta: {falta.join('; ')}.</p>}
    </form>
  );
}

function PasoEfirma({ reto, onListo }) {
  const [cer, setCer] = useState(null);
  const [key, setKey] = useState(null);
  const [contrasena, setContrasena] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState(null);
  const falta = [!cer && 'el archivo .cer', !key && 'el archivo .key', !contrasena && 'la contraseña'].filter(Boolean);

  async function firmar(ev) {
    ev.preventDefault();
    setEnviando(true);
    setError(null);
    try {
      const forge = await cargarForge();
      const { certificado, firma } = firmarReto(forge, {
        cer: await leerArchivo(cer), key: await leerArchivo(key), contrasena, reto: reto.reto,
      });
      setContrasena('');
      onListo(await api.post('/recuperacion/efirma', { reto_token: reto.reto_token, certificado, firma }));
    } catch (e) {
      setError(e instanceof ErrorDeFirma ? e.message : (e.message || 'No se pudo firmar. Revisa tus archivos y vuelve a intentarlo.'));
    } finally {
      setEnviando(false);
    }
  }

  return (
    <form onSubmit={firmar} className="space-y-3">
      <h2 className="flex items-center gap-2 font-semibold text-gray-900">
        <FileSignature className="h-5 w-5" aria-hidden="true" /> Firma con tu e.firma
      </h2>
      <label className="block text-sm font-medium text-gray-800">
        Certificado (.cer) <span className="text-red-600" aria-hidden="true">*</span>
        <input className={CAMPO} type="file" accept=".cer" onChange={(e) => setCer(e.target.files?.[0] || null)} />
      </label>
      <label className="block text-sm font-medium text-gray-800">
        Llave privada (.key) <span className="text-red-600" aria-hidden="true">*</span>
        <input className={CAMPO} type="file" accept=".key" onChange={(e) => setKey(e.target.files?.[0] || null)} />
      </label>
      <label className="block text-sm font-medium text-gray-800">
        Contraseña de la e.firma <span className="text-red-600" aria-hidden="true">*</span>
        <CampoContrasena className={CAMPO} value={contrasena} autoComplete="off"
                         onChange={(e) => setContrasena(e.target.value)} />
      </label>
      <Ayuda id="seguridad.recuperar.efirma" />
      {error && <Aviso>{error}</Aviso>}
      <button type="submit" className={PRIMARIO} disabled={enviando || falta.length > 0}>
        {enviando && <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />} Firmar y enviar
      </button>
      {falta.length > 0 && <p className="text-sm text-gray-600">Falta: {falta.join(', ')}.</p>}
    </form>
  );
}

function PasoCsf({ reto, peticion, onListo }) {
  const [pdf, setPdf] = useState(null);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState(null);

  async function subir(ev) {
    ev.preventDefault();
    setEnviando(true);
    setError(null);
    try {
      const cuerpo = { pdf: aBase64(await leerArchivo(pdf)) };
      Object.assign(cuerpo, peticion || { reto_token: reto?.reto_token });
      onListo(await api.post('/recuperacion/csf', cuerpo));
    } catch (e) {
      setError(e.message);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <form onSubmit={subir} className="space-y-3">
      <h2 className="flex items-center gap-2 font-semibold text-gray-900">
        <FileText className="h-5 w-5" aria-hidden="true" /> Constancia de Situación Fiscal reciente
      </h2>
      <label className="block text-sm font-medium text-gray-800">
        Constancia en PDF <span className="text-red-600" aria-hidden="true">*</span>
        <input className={CAMPO} type="file" accept="application/pdf,.pdf" onChange={(e) => setPdf(e.target.files?.[0] || null)} />
      </label>
      <Ayuda id="seguridad.recuperar.csf" />
      {error && <Aviso>{error}</Aviso>}
      <button type="submit" className={PRIMARIO} disabled={enviando || !pdf}>
        {enviando && <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />} Subir constancia
      </button>
      {!pdf && <p className="text-sm text-gray-600">Falta: elegir el PDF de la constancia.</p>}
    </form>
  );
}

function PasoCurp({ peticion, onListo }) {
  const [curp, setCurp] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState(null);
  const valida = curpValida(curp);

  async function enviar(ev) {
    ev.preventDefault();
    setEnviando(true);
    setError(null);
    try {
      onListo(await api.post('/recuperacion/curp', { ...peticion, curp }));
    } catch (e) {
      setError(e.message);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <form onSubmit={enviar} className="space-y-3">
      <h2 className="flex items-center gap-2 font-semibold text-gray-900">
        <IdCard className="h-5 w-5" aria-hidden="true" /> CURP del Admin Supremo
      </h2>
      <label className="block text-sm font-medium text-gray-800">
        CURP <span className="text-red-600" aria-hidden="true">*</span>
        <input className={CAMPO} value={curp} onChange={(e) => setCurp(limpiarCurp(e.target.value))}
               autoCapitalize="characters" autoComplete="off" />
      </label>
      <Ayuda id="seguridad.recuperar.curp" />
      {error && <Aviso>{error}</Aviso>}
      <button type="submit" className={PRIMARIO} disabled={enviando || !valida}>
        {enviando && <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />} Enviar CURP
      </button>
      {!valida && <p className="text-sm text-gray-600">Falta: la CURP completa, de 18 caracteres.</p>}
    </form>
  );
}

function PasoEspera({ estado, peticion, onEstado }) {
  const [password, setPassword] = useState('');
  const [correo, setCorreo] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState(null);
  const [listo, setListo] = useState(null);
  const falta = faltaParaLaRevision(estado.espera_hasta);
  const tono = estado.fase === 'DETENIDA' || estado.fase === 'RECHAZADA' ? 'rojo'
    : estado.fase === 'EN_ESPERA' ? 'amarillo' : 'verde';

  async function actualizar() {
    try { onEstado(await api.post('/recuperacion/estado', peticion)); } catch (e) { setError(e.message); }
  }

  async function guardar(ev) {
    ev.preventDefault();
    setEnviando(true);
    setError(null);
    try {
      const r = await api.post('/recuperacion/contrasena', { ...peticion, password, correo: correo || undefined });
      setListo(r);
      onEstado({ ...estado, fase: 'TERMINADA', dice: r.mensaje });
    } catch (e) {
      setError(e.message);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="space-y-3">
      <h2 className="flex items-center gap-2 font-semibold text-gray-900">
        <Hourglass className="h-5 w-5" aria-hidden="true" /> Espera y revisión
      </h2>
      <Aviso tono={tono}>{listo ? listo.mensaje : estado.dice}</Aviso>
      <ul className="space-y-1">
        {estado.pasos.map((p) => (
          <li key={p.tipo} className="flex items-center gap-2 text-sm text-gray-800">
            {p.hecho
              ? <CheckCircle2 className="h-4 w-4 text-green-700" aria-hidden="true" />
              : <Circle className="h-4 w-4 text-gray-400" aria-hidden="true" />}
            {p.nombre} {p.hecho ? '· hecho' : '· pendiente'}
          </li>
        ))}
      </ul>
      {estado.fase === 'EN_ESPERA' && (
        <>
          <p className="text-sm text-gray-700">{falta || 'La espera terminó: la revisión de un operador de TEGO está en curso.'}</p>
          <Ayuda id="seguridad.recuperar.espera" />
          <button type="button" onClick={actualizar} className={SECUNDARIO}>Ver cómo va</button>
        </>
      )}
      {estado.fase === 'APROBADA' && !listo && (
        <form onSubmit={guardar} className="space-y-3">
          <label className="block text-sm font-medium text-gray-800">
            Contraseña nueva <span className="text-red-600" aria-hidden="true">*</span>
            <CampoContrasena className={CAMPO} value={password} autoComplete="new-password"
                             onChange={(e) => setPassword(e.target.value)} />
          </label>
          <label className="block text-sm font-medium text-gray-800">
            Correo para entrar (si lo dejas vacío, el de esta petición)
            <input className={CAMPO} type="email" value={correo} autoComplete="email"
                   onChange={(e) => setCorreo(e.target.value.trim())} />
          </label>
          <Ayuda id="seguridad.recuperar.contrasena" />
          {error && <Aviso>{error}</Aviso>}
          <button type="submit" className={PRIMARIO}
                  disabled={enviando || password.length < 12 || (correo !== '' && !correoValido(correo))}>
            {enviando && <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />} Guardar y terminar
          </button>
          {password.length < 12 && <p className="text-sm text-gray-600">Falta: una contraseña de al menos 12 caracteres, con letras y números.</p>}
        </form>
      )}
      {(estado.fase === 'TERMINADA' || listo) && (
        <a href="/app?ir=login" className={PRIMARIO}>Entrar a TEGO</a>
      )}
      {error && estado.fase !== 'APROBADA' && <Aviso>{error}</Aviso>}
    </div>
  );
}

function LigaDeSeguimiento({ peticion }) {
  const [copiada, setCopiada] = useState(false);
  const temporizador = useRef(null);
  useEffect(() => () => clearTimeout(temporizador.current), []);
  const liga = ligaDeSeguimiento(window.location.origin, peticion);
  async function copiar() {
    // Peaje U-02: copiar con respaldo (portapapeles → execCommand); si el
    // aparato no permite ninguno, la liga sigue a la vista.
    if (await copiarTexto(liga)) {
      setCopiada(true);
      temporizador.current = setTimeout(() => setCopiada(false), 3000);
    }
  }
  return (
    <div className="space-y-2 rounded-lg border border-gray-200 bg-gray-50 p-3">
      <p className="text-sm font-medium text-gray-800">La liga de tu petición</p>
      <p className="break-all font-mono text-xs text-gray-700">{liga}</p>
      <button type="button" onClick={copiar} className={SECUNDARIO}>
        <Copy className="h-4 w-4" aria-hidden="true" /> {copiada ? 'Liga copiada' : 'Copiar la liga'}
      </button>
      <Ayuda id="seguridad.recuperar.liga" />
    </div>
  );
}
